import type React from "react";
import { useEffect, useRef, useState } from "react";
import "./Snake.scss";

const COLS = 20;
const ROWS = 20;
const SCALE = 20;
const CANVAS_WIDTH = COLS * SCALE;
const CANVAS_HEIGHT = ROWS * SCALE;
const UPDATE_INTERVAL = 100;

type Point = { x: number; y: number };
type Letter = Point & { char: string };

type SnakeProps = {
	data: { sourceLang: string; targetLang: string }[];
};

export const Snake: React.FC<SnakeProps> = ({ data }) => {
	const [wordIndex, setWordIndex] = useState(0);
	const source = data[wordIndex]?.sourceLang;
	const target = data[wordIndex]?.targetLang;
	const rafIdRef = useRef<number>(0);
	const runningRef = useRef(true);
	const gameStartedRef = useRef(false);

	const sourceRef = useRef(source);
	useEffect(() => {
		sourceRef.current = source;
	}, [source]);

	const [collected, setCollected] = useState("");
	const [gameWon, setGameWon] = useState(false);
	const [errors, setErrors] = useState(0);

	const snakeRef = useRef<Point[]>([
		{ x: Math.floor(COLS / 2), y: Math.floor(ROWS / 2) },
	]);
	const prevSnakeRef = useRef<Point[]>([...snakeRef.current]);
	const lettersRef = useRef<Letter[]>([]);
	const directionRef = useRef<Point>({ x: 0, y: -1 });
	const nextIndexRef = useRef(0);

	const canvasRef = useRef<HTMLCanvasElement>(null);
	const gridBufferRef = useRef<HTMLCanvasElement | null>(
		null,
	) as React.MutableRefObject<HTMLCanvasElement | null>;
	const lastTimeRef = useRef(performance.now());
	const accumulatorRef = useRef(0);

	const frame = (t: number) => {
		if (!runningRef.current) return;
		const delta = t - lastTimeRef.current;
		lastTimeRef.current = t;
		accumulatorRef.current += delta;
		while (accumulatorRef.current >= UPDATE_INTERVAL) {
			if (gameStartedRef.current) {
				updateLogic();
			}
			accumulatorRef.current -= UPDATE_INTERVAL;
		}
		renderFrame(accumulatorRef.current / UPDATE_INTERVAL);
		rafIdRef.current = requestAnimationFrame(frame);
	};

	const initLetters = () => {
		const arr: Letter[] = [];
		const lettersSrc = sourceRef?.current;
		for (let i = 0; i < lettersSrc?.length; i++) {
			let pos: Point;
			do {
				pos = {
					x: Math.floor(Math.random() * COLS),
					y: Math.floor(Math.random() * ROWS),
				};
			} while (
				arr.some((l) => l.x === pos.x && l.y === pos.y) ||
				snakeRef.current.some((s) => s.x === pos.x && s.y === pos.y)
			);
			arr.push({ x: pos.x, y: pos.y, char: lettersSrc[i] });
		}
		lettersRef.current = arr;
		nextIndexRef.current = 0;
		setCollected("");
	};

	const drawCanvas = () => {
		const buf = document.createElement("canvas");
		buf.width = COLS;
		buf.height = ROWS;
		const gtx = buf?.getContext("2d");

		if (!gtx || !buf) return;

		for (let x = 0; x < COLS; x++) {
			for (let y = 0; y < ROWS; y++) {
				if ((x + y) % 2 === 0) {
					gtx.fillStyle = "#E8F5E9";
				} else {
					gtx.fillStyle = "#C8E6C9";
				}
				gtx.fillRect(x, y, 1, 1);
			}
		}

		gridBufferRef.current = buf;

		const canvas = canvasRef.current;
		if (!canvas) return;
		canvas.width = CANVAS_WIDTH;
		canvas.height = CANVAS_HEIGHT;
	};

	const resetGame = () => {
		setWordIndex(0);
		setGameWon(false);
		snakeRef.current = [{ x: Math.floor(COLS / 2), y: Math.floor(ROWS / 2) }];
		prevSnakeRef.current = [{ ...snakeRef.current[0] }];
		initLetters();
		accumulatorRef.current = 0;
		gameStartedRef.current = false;
		setErrors(0);
		drawCanvas();
		startGame();
	};

	const stopGame = () => {
		runningRef.current = false;
		cancelAnimationFrame(rafIdRef.current);
		rafIdRef.current = 0;
	};

	const startGame = () => {
		runningRef.current = true;
		lastTimeRef.current = performance.now();
		accumulatorRef.current = 0;
		rafIdRef.current = requestAnimationFrame(frame);
	};

	// When wordIndex changes, re-generate letters (snake length preserved)
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		initLetters();
	}, [wordIndex]);

	const handleKeyDown = (e: KeyboardEvent) => {
		if (!gameStartedRef.current) {
			gameStartedRef.current = true;
		} else {
			if (e.key === "ArrowUp") directionRef.current = { x: 0, y: -1 };
			else if (e.key === "ArrowDown") directionRef.current = { x: 0, y: 1 };
			else if (e.key === "ArrowLeft") directionRef.current = { x: -1, y: 0 };
			else if (e.key === "ArrowRight") directionRef.current = { x: 1, y: 0 };
		}
	};

	const updateLogic = () => {
		if (gameWon) return;
		const snake = snakeRef.current;
		const dir = directionRef.current;
		const head: Point = {
			x: snake[0].x + dir.x,
			y: snake[0].y + dir.y,
		};

		if (head.x < 0 || head.x >= COLS || head.y < 0 || head.y >= ROWS) {
			setErrors((e) => e + 1);
			stopGame();
		}
		if (snake.some((s) => s.x === head.x && s.y === head.y)) {
			setErrors((e) => e + 1);
			stopGame();
		}

		prevSnakeRef.current = snake.map((p) => ({ ...p }));
		const newSnake = [head, ...snake];

		let ate = false;
		const letters = lettersRef.current;
		const lettersSrc = sourceRef.current;
		for (let i = 0; i < letters.length; i++) {
			const { x, y, char } = letters[i];
			if (x === head.x && y === head.y) {
				if (char === lettersSrc[nextIndexRef.current]) {
					nextIndexRef.current++;
					setCollected((c) => c + char);
					letters?.splice(i, 1);
					ate = true;

					if (nextIndexRef.current === lettersSrc.length) {
						if (wordIndex < data.length - 1) {
							setWordIndex((idx) => idx + 1);
						} else {
							setGameWon(true);
						}
					}
				}
				break;
			}
		}
		if (!ate) newSnake.pop();
		snakeRef.current = newSnake;
	};

	const renderFrame = (interp: number) => {
		const canvas = canvasRef.current as HTMLCanvasElement;
		const ctx = canvas?.getContext("2d");
		if (!ctx || !canvas) return;
		ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
		ctx.imageSmoothingEnabled = false;
		ctx.resetTransform();
		ctx.scale(SCALE, SCALE);
		if (!gridBufferRef.current) return;
		ctx.drawImage(gridBufferRef.current, 0, 0);

		drawSnakeBody(ctx, interp);
		drawSnakeHead(ctx, interp);

		for (const i in lettersRef.current) {
			const { x, y, char } = lettersRef.current[i];
			ctx.fillStyle = "red";
			ctx.fillRect(x, y, 1, 1);
			ctx.fillStyle = "white";
			ctx.font = "1px sans-serif";
			ctx.textAlign = "center";
			ctx.fillText(char, x + 0.5, y + 0.75);
		}
	};

	function drawSnakeHead(ctx: CanvasRenderingContext2D, interp: number) {
		const seg = snakeRef.current[0];
		const prev = prevSnakeRef.current[0] || seg;
		const x = prev.x + (seg.x - prev.x) * interp + 0.5;
		const y = prev.y + (seg.y - prev.y) * interp + 0.5;

		ctx.fillStyle = "green";
		ctx.beginPath();
		ctx.arc(x, y, 0.5, 0, Math.PI * 2);
		ctx.fill();
	}

	function drawSnakeBody(ctx: CanvasRenderingContext2D, interp: number) {
		const segments = snakeRef.current;
		const prevSegs = prevSnakeRef.current;
		const total = segments.length;
		const maxRad = 0.5;
		const minRad = 0.1;

		segments.forEach((seg, i) => {
			const prev = prevSegs[i] || seg;
			const x = prev.x + (seg.x - prev.x) * interp + 0.5;
			const y = prev.y + (seg.y - prev.y) * interp + 0.5;

			const t = i / (total - 1);
			const rad = maxRad - (maxRad - minRad) * t;

			ctx.fillStyle = i === 0 ? "green" : "blue";
			ctx.beginPath();
			ctx.arc(x, y, rad, 0, Math.PI * 2);
			ctx.fill();
		});
	}

	// Main loop
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		drawCanvas();
		resetGame();
		window.addEventListener("keydown", handleKeyDown);
		rafIdRef.current = requestAnimationFrame(frame);
		return () => {
			cancelAnimationFrame(rafIdRef.current);
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	return (
		<div className="snake__game">
			<div className="snake__ui">
				<div className="snake__info">
					<div className="snake__target">{target} - </div>
					<div className="snake__collected">
						{source?.split("").map((letter: string, i: number) => (
							<span key={letter} className="snake__letter-slot">
								{collected[i] || " "}
							</span>
						))}
					</div>
				</div>
				{gameWon && <div className="game__won">You Win!</div>}
				<button className="snake__restart" type="button" onClick={resetGame}>
					Restart
				</button>
			</div>
			<canvas
				ref={canvasRef}
				className="snake__canvas"
				style={{
					width: CANVAS_WIDTH,
					height: CANVAS_HEIGHT,
					border: "2px solid black",
				}}
			/>
		</div>
	);
};
